import React from 'react';

import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import useHistoricoCustosProduto from './useHistoricoCustosProduto';

const formatarMoeda = (valor) => {
    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
        return 'R$ 0,00';
    }

    return numero.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
};

const formatarNumero = (valor, casas = 3) => {
    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
        return '0';
    }

    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: casas,
    });
};

const formatarData = (valor) => {
    if (!valor) {
        return '-';
    }

    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
        return valor;
    }

    return data.toLocaleDateString('pt-BR');
};

const formatarVariacao = (valor) => {
    const numero = Number(valor);

    if (!Number.isFinite(numero)) {
        return '-';
    }

    const sinal = numero > 0 ? '+' : '';

    return `${sinal}${numero.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}%`;
};

export default function HistoricoCustosProduto({
    empresaId,
    produtoId,
    movimentoIdIgnorar,
    movimentoItemIdIgnorar,
    limite = 10,
}) {
    const { historico, loading, error } = useHistoricoCustosProduto({
        empresaId,
        produtoId,
        movimentoIdIgnorar,
        movimentoItemIdIgnorar,
        limite,
    });

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    padding: 2,
                }}
            >
                <CircularProgress size={20} />

                <Typography variant="body2">Carregando últimos custos...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ width: '100%', padding: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (historico.length === 0) {
        return (
            <Box sx={{ width: '100%', padding: 2 }}>
                <Alert severity="info">Nenhuma entrada com custo foi localizada para este produto.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ marginBottom: 1 }}>
                Últimos custos do produto
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Empresa</TableCell>
                            <TableCell>Fornecedor</TableCell>
                            <TableCell>Operação</TableCell>
                            <TableCell>Embalagem</TableCell>

                            <TableCell align="right">Quantidade</TableCell>

                            <TableCell align="right">Preço</TableCell>

                            <TableCell align="right">Unitário</TableCell>

                            <TableCell align="right">Custo</TableCell>

                            <TableCell align="right">Var. preço</TableCell>

                            <TableCell align="right">Var. custo</TableCell>

                            <TableCell align="right">Total</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {historico.map((item) => (
                            <TableRow key={item.uuid ?? item.id}>
                                <TableCell>{formatarData(item.data)}</TableCell>

                                <TableCell>{item.empresa ?? '-'}</TableCell>

                                <TableCell>{item.fornecedor ?? '-'}</TableCell>

                                <TableCell>{item.operacao ?? '-'}</TableCell>

                                <TableCell>{item.embalagem ?? '-'}</TableCell>

                                <TableCell align="right">{formatarNumero(item.quantidade)}</TableCell>

                                <TableCell align="right">{formatarMoeda(item.precoNegociado)}</TableCell>

                                <TableCell align="right">{formatarMoeda(item.precoPorUnidade)}</TableCell>

                                <TableCell align="right">
                                    {formatarMoeda(Number(item.custoCalculado) > 0 ? item.custoCalculado : item.precoPorUnidade)}
                                </TableCell>

                                <TableCell align="right">
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                Number(item.variacaoPrecoPercentual) > 0
                                                    ? 'error.main'
                                                    : Number(item.variacaoPrecoPercentual) < 0
                                                      ? 'success.main'
                                                      : 'text.secondary',
                                        }}
                                    >
                                        {formatarVariacao(item.variacaoPrecoPercentual)}
                                    </Typography>
                                </TableCell>

                                <TableCell align="right">
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                Number(item.variacaoCustoPercentual) > 0
                                                    ? 'error.main'
                                                    : Number(item.variacaoCustoPercentual) < 0
                                                      ? 'success.main'
                                                      : 'text.secondary',
                                        }}
                                    >
                                        {formatarVariacao(item.variacaoCustoPercentual)}
                                    </Typography>
                                </TableCell>

                                <TableCell align="right">{formatarMoeda(item.valorTotal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
